import * as React from "react";
import { Img } from "./Img.js";
import { mediaManager } from "../lib/media-manager.js";

const VIDEO_CDN = import.meta.env.VITE_VIDEO_CDN as string | undefined;

export interface VideoProps {
  /**
   * Video key within the CDN bucket (e.g. "games/intro.mp4").
   * Resolved against VITE_VIDEO_CDN to build the full URL.
   * Can also be an absolute https:// URL.
   */
  src: string;
  /** Poster image path, routed through the Img pipeline for AVIF/WebP. */
  poster?: string | null;
  /** MIME type for the <source> element. */
  type?: string;
  /** Alt text / accessible label. */
  alt?: string;
  /** When true, the video loads and plays immediately (hero/above-fold). */
  priority?: boolean;
  /** Enable native loop. */
  loop?: boolean;
  /** Show native controls. Default false for ambient/cutscene video. */
  controls?: boolean;
  /** Mute audio. Default true (autoplay requires muted). */
  muted?: boolean;
  className?: string;
  style?: React.CSSProperties;
  /** Called when the video finishes playing. */
  onEnded?: () => void;
}

/**
 * Lazy-loaded video component backed by CloudFront/S3.
 *
 * - `preload="none"` by default — the browser downloads nothing until the
 *   video scrolls into view (IntersectionObserver) or `priority` is set.
 * - Shows a responsive poster via the Img component while waiting.
 * - When the video enters the viewport, sets `preload="auto"` and starts
 *   loading. It pauses when scrolled offscreen or the tab is hidden, and (for
 *   muted/autoplay video) resumes on return. Buffered data is released by the
 *   shared media manager when too many videos buffer at once, or on unmount.
 * - Supports HLS via native <video> if the src ends in `.m3u8`.
 */
export function Video({
  src,
  poster,
  type = "video/mp4",
  alt,
  priority = false,
  loop = false,
  controls = false,
  muted = true,
  className,
  style,
  onEnded,
}: VideoProps): React.ReactElement {
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const containerRef = React.useRef<HTMLSpanElement>(null);
  // `isVisible` is a one-way latch: it flips true the first time the card nears
  // the viewport and stays true, so the <video> mounts once and never has to
  // re-buffer. `inView` tracks the *live* intersection (both directions) so
  // playback can pause off-screen and resume on return.
  const [isVisible, setIsVisible] = React.useState(priority);
  const [inView, setInView] = React.useState(priority);
  const [isPlaying, setIsPlaying] = React.useState(false);

  const resolvedSrc = src.startsWith("http") ? src : VIDEO_CDN ? `${VIDEO_CDN}/${src}` : src;

  const isHLS = resolvedSrc.endsWith(".m3u8");

  // Track viewport intersection: latch `isVisible` on first entry (mounts the
  // <video>) and keep `inView` in sync both ways so the reconcile effect below
  // can pause off-screen and resume on return.
  React.useEffect(() => {
    if (priority) return;

    const el = containerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const intersecting = !!entry?.isIntersecting;
        setInView(intersecting);
        if (intersecting) setIsVisible(true);
      },
      { rootMargin: "200px" },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [priority]);

  // Register with the shared media manager for the element's lifetime, so the
  // concurrent-buffering cap can account for it. Unregister only on unmount —
  // not on every scroll — so returning to the card doesn't re-buffer.
  React.useEffect(() => {
    if (!isVisible) return;
    const video = videoRef.current;
    if (!video) return;

    video.preload = "auto";
    mediaManager.register(video);

    return () => {
      mediaManager.unregister(video);
    };
  }, [isVisible]);

  // Reconcile playback against whether the video is on-screen with the tab in
  // the foreground. Pausing on exit (or tab-hide) runs for *every* video, which
  // preserves the "pauses when offscreen" contract. Resuming is gated on `muted`
  // because only muted video may play without a user gesture (autoplay policy) —
  // an unmuted/controls video is left for the user to start. The resume path is
  // the actual fix: the previous one-shot play() never recovered from a pause,
  // so the reel froze on its poster after scrolling away or switching tabs.
  React.useEffect(() => {
    if (!isVisible) return;
    const video = videoRef.current;
    if (!video) return;

    const reconcile = () => {
      const tabVisible = typeof document === "undefined" || document.visibilityState === "visible";
      if (inView && tabVisible) {
        // `!video.ended` so a finished non-looping video isn't restarted on
        // every tab-return — `ended` also reads as `paused`, and the old
        // one-shot behaviour left it stopped. (Looping video never ends.)
        if (muted && video.paused && !video.ended) {
          void video
            .play()
            .then(() => setIsPlaying(true))
            .catch(() => {});
        }
      } else if (!video.paused) {
        video.pause();
      }
    };

    reconcile();
    document.addEventListener("visibilitychange", reconcile);
    return () => document.removeEventListener("visibilitychange", reconcile);
  }, [isVisible, inView, muted]);

  const wrapperStyle: React.CSSProperties = {
    position: "relative",
    display: "block",
    overflow: "hidden",
    ...style,
  };

  return (
    <span ref={containerRef} className={className} style={wrapperStyle}>
      {!isPlaying && poster && (
        <Img
          src={poster}
          alt={alt ?? ""}
          priority={priority}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
      )}

      {isVisible && (
        <video
          ref={videoRef}
          preload={priority ? "auto" : "none"}
          loop={loop}
          controls={controls}
          muted={muted}
          playsInline
          aria-label={alt}
          onEnded={onEnded}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          style={{
            display: "block",
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        >
          {isHLS ? (
            <source src={resolvedSrc} type="application/x-mpegURL" />
          ) : (
            <source src={resolvedSrc} type={type} />
          )}
        </video>
      )}
    </span>
  );
}
